import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Sewp.css';

// SEWP VI requires a separate page for the prime (Inalab) and for each JV it is
// part of (InaHarbor2), so each contract holder gets its own entry and route.
const MAIN_PHONE = '703-459-1079';
const MAIN_FAX = '703-636-4893';

const SEWP_HOLDERS = {
  inalab: {
    path: '/sewp',
    pageTitle: 'NASA SEWP VI | Inalab Consulting, Inc.',
    holderName: 'Inalab Consulting, Inc.',
    shortName: 'Inalab',
    about:
      'Inalab has 15+ years of experience delivering information technology, digital transformation, enterprise-wide strategic solutions, and mission-based support services to federal clients, including Defense Health Agency, US Army, US Air Force, Department of State, and Department of Homeland Security.',
    contracts: [
      { category: 'B', number: '80TECH26D0511' },
      { category: 'C', number: '80TECH26D1076' },
    ],
    pop: 'November 1, 2026 through October 31, 2036',
    surcharge: '0.34%',
    uei: 'KW7MEJXRX4H8',
    address: ['8614 Westwood Center Drive, Suite 410', 'Vienna, VA 22182'],
    programContacts: [
      { name: 'Kate Alexandrova', title: 'SEWP Program Manager', email: 'kalexandrova@inalabgroup.com', phone: MAIN_PHONE, ext: '703' },
      { name: 'Elizabeth Davis', title: 'SEWP Deputy Program Manager', email: 'edavis@inalabgroup.com', phone: MAIN_PHONE, ext: '713' },
    ],
    salesContacts: [
      { name: 'Niraj Kathuria', title: 'EVP of Business Development', email: 'nkathuria@inalabgroup.com', phone: MAIN_PHONE, ext: '709' },
      { name: 'Nathan Wright', title: 'Sr. Capture/Proposal Manager', email: 'nwright@inalabgroup.com', phone: MAIN_PHONE, ext: '722' },
    ],
    salesEmail: 'sales@inalabgroup.com',
    orderSupportContacts: [
      { name: 'Kate Alexandrova', title: 'SEWP Program Manager', email: 'kalexandrova@inalabgroup.com', phone: MAIN_PHONE, ext: '703' },
      { name: 'Elizabeth Davis', title: 'SEWP Deputy Program Manager', email: 'edavis@inalabgroup.com', phone: MAIN_PHONE, ext: '713' },
    ],
    orderingGuide: '/inalab_sewp_vi_ordering_guide.pdf',
    relatedPage: { to: '/sewp/inaharbor2', label: 'InaHarbor2 LLC (joint venture) SEWP VI page' },
  },
  inaharbor2: {
    path: '/sewp/inaharbor2',
    pageTitle: 'NASA SEWP VI | InaHarbor2 LLC',
    holderName: 'InaHarbor2 LLC',
    shortName: 'InaHarbor2',
    about:
      'InaHarbor2 is an Alaska Native 8(a) joint venture between Nash Harbor Solutions and Inalab Consulting, Inc. InaHarbor2 has 15+ years of experience delivering information technology, digital transformation, enterprise-wide strategic solutions, and mission-based support services to federal clients, including Defense Health Agency, US Army, US Air Force, Department of State, and Department of Homeland Security.',
    contracts: [
      { category: 'B', number: '80TECH26D1997' },
    ],
    pop: 'November 1, 2026 through October 31, 2036',
    surcharge: '0.34%',
    uei: 'X84ZUPTVCRP4',
    address: ['6400 Arlington Boulevard, Suite 725A', 'Falls Church, VA 22042'],
    programContacts: [
      { name: 'Elizabeth Davis', title: 'SEWP Program Manager', email: 'edavis@inaharbor.com', phone: MAIN_PHONE, ext: '713' },
      { name: 'Kate Alexandrova', title: 'SEWP Deputy Program Manager', email: 'kalexandrova@inaharbor.com', phone: MAIN_PHONE, ext: '703' },
    ],
    salesContacts: [
      { name: 'Niraj Kathuria', title: 'EVP of Business Development', email: 'nkathuria@inaharbor.com', phone: MAIN_PHONE, ext: '709' },
      { name: 'Nathan Wright', title: 'Sr. Capture/Proposal Manager', email: 'nwright@inaharbor.com', phone: MAIN_PHONE, ext: '722' },
    ],
    orderSupportContacts: [
      { name: 'Elizabeth Davis', title: 'SEWP Program Manager', email: 'edavis@inaharbor.com', phone: MAIN_PHONE, ext: '713' },
      { name: 'Kate Alexandrova', title: 'SEWP Deputy Program Manager', email: 'kalexandrova@inaharbor.com', phone: MAIN_PHONE, ext: '703' },
    ],
    orderingGuide: '/inaharbor2_sewp_vi_ordering_guide.pdf',
    relatedPage: { to: '/sewp', label: 'Inalab Consulting, Inc. (prime) SEWP VI page' },
  },
};

function telHref(phone, ext) {
  const digits = phone.replace(/[^0-9]/g, '');
  return `tel:+1${digits}${ext ? `,${ext}` : ''}`;
}

function ContactList({ contacts }) {
  return (
    <ul className="sewp-contact-list">
      {contacts.map((c, i) => (
        <li className="sewp-contact" key={i}>
          <span className="sewp-contact-name">{c.name}</span>
          <span className="sewp-contact-title">{c.title}</span>
          <a className="sewp-contact-link" href={`mailto:${c.email}`}>{c.email}</a>
          <a className="sewp-contact-link" href={telHref(c.phone, c.ext)}>
            {c.phone}{c.ext ? ` x${c.ext}` : ''}
          </a>
        </li>
      ))}
    </ul>
  );
}

export default function Sewp({ holder = 'inalab' }) {
  const h = SEWP_HOLDERS[holder];

  useEffect(() => {
    document.title = h.pageTitle;
    window.scrollTo(0, 0);
  }, [h]);

  const categories = h.contracts.map(c => c.category).join(' and ');

  return (
    <>
      <div className="page-banner" style={{ backgroundImage: 'url(/contract_banner.webp)' }}>
        <h1 className="page-banner-title" id="sewp-heading">
          NASA SEWP VI
          <span className="sewp-banner-holder">{h.holderName}</span>
        </h1>
      </div>

      <main className="sewp-page-wrapper" aria-labelledby="sewp-heading">
        <section className="sewp-section" aria-labelledby="sewp-overview-heading">
          <div className="sewp-section-header">
            <h2 id="sewp-overview-heading" className="sewp-section-heading">Program Overview</h2>
            <Link to="/contract-vehicles" className="sewp-back-link">
              &larr; Back to Contract Vehicles
            </Link>
          </div>
          <div className="sewp-overview-body">
            <div className="sewp-overview-copy">
              <p className="sewp-text">
                NASA SEWP (Solutions for Enterprise-Wide Procurement), pronounced &ldquo;soup,&rdquo;
                provides the latest Information Technology, Communication, and Audio-Visual (ITC/AV)
                solutions and services for all Federal agencies and their approved contractors. SEWP is a
                multi-award Government-Wide Acquisition Contract (GWAC) that represents acquisition
                innovation within the Federal Government, self-funded through a low usage fee and
                supporting more than 50,000 orders a year.
              </p>
              <p className="sewp-text">
                {h.holderName} holds {h.contracts.length > 1 ? 'SEWP VI contracts in Categories' : 'a SEWP VI contract in Category'}{' '}
                {categories}. As a multi-award GWAC, orders are placed in accordance with the Fair
                Opportunity provisions of FAR 16.505(b) (see below).
              </p>
            </div>
            <img
              className="sewp-badge"
              src="/sewp_vi_contract_holder_logo.jpg"
              width="600"
              height="388"
              alt="NASA SEWP VI Solutions for Enterprise-Wide Procurement Contract Holder"
            />
          </div>
        </section>

        <section className="sewp-section" aria-labelledby="sewp-about-heading">
          <h2 id="sewp-about-heading" className="sewp-section-heading">About {h.holderName}</h2>
          <p className="sewp-text">{h.about}</p>
        </section>

        <section className="sewp-section" aria-labelledby="sewp-facts-heading">
          <h2 id="sewp-facts-heading" className="sewp-section-heading">Contract Information</h2>
          <dl className="sewp-facts-grid">
            <div className="sewp-fact"><dt>Contract Holder</dt><dd>{h.holderName}</dd></div>
            {h.contracts.map(c => (
              <div className="sewp-fact" key={c.number}>
                <dt>Contract Number (Category {c.category})</dt>
                <dd>{c.number}</dd>
              </div>
            ))}
            <div className="sewp-fact"><dt>Contract Type</dt><dd>GWAC</dd></div>
            <div className="sewp-fact"><dt>Period of Performance</dt><dd>{h.pop}</dd></div>
            <div className="sewp-fact"><dt>SEWP Surcharge</dt><dd>{h.surcharge}</dd></div>
            <div className="sewp-fact"><dt>UEI</dt><dd>{h.uei}</dd></div>
            <div className="sewp-fact">
              <dt>Main Office</dt>
              <dd>{h.address.map((line, i) => <span className="sewp-address-line" key={i}>{line}</span>)}</dd>
            </div>
            <div className="sewp-fact">
              <dt>Main Office Phone / Fax</dt>
              <dd>
                <span className="sewp-address-line">
                  Phone: <a className="sewp-contact-link" href={telHref(MAIN_PHONE)}>{MAIN_PHONE}</a>
                </span>
                <span className="sewp-address-line">Fax: {MAIN_FAX}</span>
              </dd>
            </div>
          </dl>
        </section>

        <section className="sewp-section" aria-labelledby="sewp-guide-heading">
          <h2 id="sewp-guide-heading" className="sewp-section-heading">Ordering Guide</h2>
          <p className="sewp-text">
            Download the {h.shortName} SEWP VI Ordering Guide for a printable overview of SEWP, fair
            opportunity, contact information, what&rsquo;s in scope, and the ordering process.
          </p>
          <a className="sewp-button" href={h.orderingGuide} target="_blank" rel="noopener noreferrer">
            Download {h.shortName} Ordering Guide (PDF, opens in new tab)
          </a>
        </section>

        <section className="sewp-section" aria-labelledby="sewp-pm-heading">
          <h2 id="sewp-pm-heading" className="sewp-section-heading">Program Management Contacts</h2>
          <ContactList contacts={h.programContacts} />
        </section>

        <section className="sewp-section" aria-labelledby="sewp-support-heading">
          <h2 id="sewp-support-heading" className="sewp-section-heading">Program Support</h2>

          <h3 className="sewp-subheading">How to Obtain a Quote</h3>
          <p className="sewp-text">
            For a quote on hardware, software, or services, contact our quote team below.
            You may also submit a request through the NASA SEWP online Quote Request Tool (QRT).
          </p>
          <ContactList contacts={h.salesContacts} />
          {h.salesEmail && (
            <p className="sewp-text">
              Quote requests may also be sent to{' '}
              <a className="sewp-inline-link" href={`mailto:${h.salesEmail}`}>{h.salesEmail}</a>.
            </p>
          )}

          <h3 className="sewp-subheading">Order Issues and Post-Delivery Support</h3>
          <p className="sewp-text">
            To troubleshoot a problematic order or for any post-delivery issues, contact our order
            support staff below.
          </p>
          <ContactList contacts={h.orderSupportContacts} />
        </section>

        <section className="sewp-section" aria-labelledby="sewp-fair-heading">
          <h2 id="sewp-fair-heading" className="sewp-section-heading">Fair Opportunity</h2>
          <p className="sewp-text">
            FAR 16.505(b)(1) provides that each contractor shall be given fair opportunity to be
            considered for each order exceeding $10,000 and issued under multiple award contracts. The
            FAR states that the method to obtain fair opportunity is at the discretion of the
            Contracting Officer (CO) and that the CO must document the rationale for placement and price
            of each order. Using the SEWP online Quote Request Tool is the recommended method to assist
            in this activity and to augment the required decision documentation. The SEWP Quote Request
            Tool (QRT) will automatically include the Contract Holders within a selected Group or based
            on a suggested source.
          </p>
        </section>

        <section className="sewp-section" aria-labelledby="sewp-links-heading">
          <h2 id="sewp-links-heading" className="sewp-section-heading">Related Resources</h2>
          <ul className="sewp-links-list">
            <li>
              <a className="sewp-inline-link" href="https://www.sewp.nasa.gov/" target="_blank" rel="noopener noreferrer">
                NASA SEWP home (www.sewp.nasa.gov, opens in new tab)
              </a>
            </li>
            <li>
              <Link className="sewp-inline-link" to={h.relatedPage.to}>{h.relatedPage.label}</Link>
            </li>
            <li>
              <Link className="sewp-inline-link" to="/contract-vehicles">All Inalab contract vehicles</Link>
            </li>
          </ul>
          <p className="sewp-text sewp-help">
            NASA SEWP Help Desk: <a className="sewp-inline-link" href="mailto:help@sewp.nasa.gov">help@sewp.nasa.gov</a>{' '}
            &middot; (301) 286-1478 &middot; Mon&ndash;Fri, 7:30 AM&ndash;6 PM ET
          </p>
        </section>
      </main>
    </>
  );
}
